import { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OBJECTIFS = [
    { fr: "Perte de gras", en: "Fat loss" },
    { fr: "Prise de masse musculaire", en: "Muscle gain" },
    { fr: "Rééquilibrage alimentaire", en: "Diet rebalancing" },
    { fr: "Autre", en: "Other" },
];

const REPAS = [
    { fr: "1-2 repas", en: "1-2 meals" },
    { fr: "3 repas", en: "3 meals" },
    { fr: "4 repas", en: "4 meals" },
    { fr: "5 repas et plus", en: "5+ meals" },
];

const inputCls =
    "w-full rounded-md border border-white/15 bg-obsidian px-4 py-3 text-cream placeholder:text-sand/50 focus:border-terracotta focus:outline-none transition-colors duration-300";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-sand font-medium">{label}</span>
        <div className="mt-2">{children}</div>
    </label>
);

const NutritionForm = () => {
    const { tr } = useLang();
    const [form, setForm] = useState({
        prenom: "", nom: "", telephone: "", courriel: "",
        objectif: "Perte de gras", plats: "", restrictions: "", repas: "3 repas", message: "",
    });
    const [status, setStatus] = useState("idle");
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const valid = form.prenom.trim() && form.nom.trim() && form.telephone.trim() && form.courriel.includes("@");

    const submit = async (e) => {
        e.preventDefault();
        if (!valid || status === "loading") return;
        setStatus("loading");
        try {
            const r = await fetch(`${API}/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!r.ok) throw new Error();
            setStatus("done");
        } catch {
            setStatus("error");
        }
    };

    if (status === "done") {
        return (
            <div className="text-center py-10" data-testid="nutrition-form-success">
                <CheckCircle2 className="h-12 w-12 text-whatsapp mx-auto" />
                <h3 className="mt-5 font-heading text-2xl font-bold tracking-tight">{tr("C'est envoyé, merci !", "Sent, thank you!")}</h3>
                <p className="mt-3 text-sand font-light max-w-md mx-auto">
                    {tr(
                        `Coach Roméo a bien reçu ton questionnaire. Il te contactera personnellement très bientôt au ${form.telephone} ou par courriel.`,
                        `Coach Roméo has received your questionnaire. He will contact you personally very soon at ${form.telephone} or by email.`
                    )}
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="nutrition-form">
            <Field label={tr("Prénom *", "First name *")}>
                <input data-testid="form-prenom" className={inputCls} value={form.prenom} onChange={set("prenom")} placeholder={tr("Ton prénom", "Your first name")} required />
            </Field>
            <Field label={tr("Nom *", "Last name *")}>
                <input data-testid="form-nom" className={inputCls} value={form.nom} onChange={set("nom")} placeholder={tr("Ton nom", "Your last name")} required />
            </Field>
            <Field label={tr("Numéro de téléphone *", "Phone number *")}>
                <input data-testid="form-telephone" type="tel" className={inputCls} value={form.telephone} onChange={set("telephone")} placeholder="819 555 1234" required />
            </Field>
            <Field label={tr("Courriel *", "Email *")}>
                <input data-testid="form-courriel" type="email" className={inputCls} value={form.courriel} onChange={set("courriel")} placeholder="you@email.com" required />
            </Field>
            <Field label={tr("Ton objectif principal", "Your main goal")}>
                <select data-testid="form-objectif" className={inputCls} value={form.objectif} onChange={set("objectif")}>
                    {OBJECTIFS.map((o) => (
                        <option key={o.fr} value={o.fr}>{tr(o)}</option>
                    ))}
                </select>
            </Field>
            <Field label={tr("Repas par jour", "Meals per day")}>
                <select data-testid="form-repas" className={inputCls} value={form.repas} onChange={set("repas")}>
                    {REPAS.map((o) => (
                        <option key={o.fr} value={o.fr}>{tr(o)}</option>
                    ))}
                </select>
            </Field>
            <div className="md:col-span-2">
                <Field label={tr("Quels sont tes plats africains préférés ?", "What are your favourite African dishes?")}>
                    <textarea data-testid="form-plats" rows={3} className={inputCls} value={form.plats} onChange={set("plats")} placeholder={tr("Ex. mafé, thiéboudienne, ndolé, poulet braisé, attiéké…", "E.g. mafé, thiéboudienne, ndolé, grilled chicken, attiéké…")} />
                </Field>
            </div>
            <div className="md:col-span-2">
                <Field label={tr("Allergies ou restrictions alimentaires ?", "Allergies or dietary restrictions?")}>
                    <input data-testid="form-restrictions" className={inputCls} value={form.restrictions} onChange={set("restrictions")} placeholder={tr("Ex. arachides, lactose, végétarien, aucune…", "E.g. peanuts, lactose, vegetarian, none…")} />
                </Field>
            </div>
            <div className="md:col-span-2">
                <Field label={tr("Un mot pour le coach ? (optionnel)", "A word for the coach? (optional)")}>
                    <textarea data-testid="form-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder={tr("Parle-moi de ta situation, ton historique, tes envies…", "Tell me about your situation, your history, your goals…")} />
                </Field>
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row items-center gap-4">
                <button
                    type="submit"
                    data-testid="form-submit-button"
                    disabled={!valid || status === "loading"}
                    className="inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {tr("Envoyer au coach", "Send to the coach")}
                </button>
                {status === "error" && (
                    <p className="text-sm text-terracotta" data-testid="form-error">
                        {tr("Une erreur est survenue — réessaie ou écris-moi par courriel.", "Something went wrong — try again or email me.")}
                    </p>
                )}
                <p className="text-xs text-sand">
                    {tr("Tes infos restent entre nous. Le coach te rappelle personnellement.", "Your info stays between us. The coach calls you back personally.")}
                </p>
            </div>
        </form>
    );
};

export default NutritionForm;
